/**
 * ResultEntryPage — set-by-set result entry at /games/:gameId/result.
 *
 * Layout: teams as rows, games (G1/G2/G3) as columns, score cells as number inputs.
 * The point system (21-point / 15-point) comes from the game's division; ALL
 * scoring rules live in the shared `@sulis-liiga/common` badminton module. The
 * winner is auto-derived from the set scores (never picked manually), entering a
 * losing score autofills the winning score, and the deciding game is hidden until
 * the match is level.
 *
 * The form holds the two teams positionally as `a`/`b` (matching the order of the
 * game's `teamIds`) — there is no home/away distinction — and maps them back to
 * team ids on submit.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { AppDispatch, RootState } from '../../store';
import { fetchGameById, submitGameResult } from '../../store/slices/gameSlice';
import { GameStatus } from '@sulis-liiga/common';
import { teamLabel } from '../../utils/gameTeams';
import ScoreEntry, { type ScoreEntryResult } from '../../components/game/ScoreEntry';

const ResultEntryPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { gameId } = useParams<{ gameId: string }>();

  const { currentGame: game, loading, error } = useSelector((state: RootState) => state.game);

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // The derived result reported by the shared ScoreEntry grid.
  const [result, setResult] = useState<ScoreEntryResult>({
    setWins: { a: 0, b: 0 },
    winner: null,
    sets: [],
    hasErrors: false,
    isComplete: false,
    retirement: { isRetirement: false, retiredSide: null },
  });

  useEffect(() => {
    if (gameId) dispatch(fetchGameById(gameId));
  }, [dispatch, gameId]);

  const handleResultChange = useCallback((r: ScoreEntryResult) => setResult(r), []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!game) return;
    const { isRetirement, retiredSide } = result.retirement;
    if (!isRetirement && result.hasErrors) {
      setSubmitError('Please fix the score validation errors before submitting.');
      return;
    }
    if (isRetirement && !retiredSide) {
      setSubmitError('Please choose which team retired.');
      return;
    }
    if (!isRetirement && !result.winner) {
      setSubmitError(
        'The winner cannot be determined from the scores entered. Please check the scores.',
      );
      return;
    }
    setSubmitting(true);
    setSubmitError(null);

    const [teamAId, teamBId] = game.teamIds;
    const retiredTeamId = isRetirement ? (retiredSide === 'a' ? teamAId : teamBId) : undefined;

    try {
      await dispatch(
        submitGameResult({
          gameId: gameId ?? '',
          result: {
            scores: { [teamAId]: result.setWins.a, [teamBId]: result.setWins.b },
            sets: result.sets.map(s => ({ [teamAId]: s.a, [teamBId]: s.b })),
            ...(retiredTeamId ? { retiredTeamId } : {}),
          },
        }),
      ).unwrap();
      // Return to the game hub — it shows the submitted result and a
      // "waiting for the other team to approve" message. The /approve page is
      // the OTHER team's view, so the submitter should not land there.
      navigate(`/games/${gameId}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading / Error states ─────────────────────────────────────────────────

  if (loading && !game) {
    return (
      <div className="flex items-center justify-center py-20">
        <svg
          className="animate-spin h-10 w-10 text-primary-600"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-label="Loading"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    );
  }

  if (error && !game) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="rounded-md bg-danger-50 p-4" role="alert">
          <p className="text-sm text-danger-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="max-w-2xl mx-auto text-center py-20">
        <p className="text-gray-500">Game not found.</p>
        <Link to="/games" className="mt-3 text-sm text-primary-600 hover:underline">
          Back to Games
        </Link>
      </div>
    );
  }

  if (game.status !== GameStatus.SCHEDULED) {
    return (
      <div className="max-w-2xl mx-auto">
        <Link to={`/games/${gameId}`} className="text-sm text-primary-600">
          &larr; Back to game
        </Link>
        <div className="mt-4 rounded-md bg-yellow-50 p-4">
          <p className="text-sm text-yellow-800">
            Result can only be entered for scheduled games. This game is{' '}
            <strong>{game.status}</strong>.
          </p>
        </div>
      </div>
    );
  }

  const teamALabel = teamLabel(game, game.teamIds[0]);
  const teamBLabel = teamLabel(game, game.teamIds[1]);

  return (
    <div className="max-w-2xl mx-auto">
      <Link to={`/games/${gameId}`} className="text-sm text-primary-600 hover:text-primary-700">
        &larr; Back to game
      </Link>
      <h1 className="mt-2 text-3xl font-bold text-gray-900">Enter result</h1>

      {/* Game header */}
      <section className="mt-4 bg-white rounded-lg shadow-sm p-5">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500">{game.divisionName ?? 'Scheduled game'}</div>
          <span className="text-xs px-2 py-0.5 rounded-full bg-success-100 text-success-800">
            Scheduled
          </span>
        </div>
        <div className="mt-1 text-lg font-semibold text-gray-900">
          {teamALabel} <span className="text-gray-400 font-normal">vs</span> {teamBLabel}
        </div>
        <div className="mt-1 text-sm text-gray-500">
          {game.scheduledDate
            ? new Date(game.scheduledDate).toLocaleDateString('en-GB', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })
            : ''}
          {game.scheduledTime ? `, ${game.scheduledTime}` : ''}
          {game.venue?.name ? ` · ${game.venue.name}` : ''}
          {game.venue?.address ? `, ${game.venue.address}` : ''}
        </div>
      </section>

      {/* Score entry */}
      <form onSubmit={handleSubmit} noValidate>
        <ScoreEntry
          pointSystem={game.divisionPointSystem}
          teamALabel={teamALabel}
          teamBLabel={teamBLabel}
          onChange={handleResultChange}
        />

        {/* Error */}
        {submitError && (
          <div className="mt-4 rounded-md bg-danger-50 p-4" role="alert">
            <p className="text-sm text-danger-600">{submitError}</p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex items-center justify-end gap-3">
          <Link
            to={`/games/${gameId}`}
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </Link>
          <button
            type="submit"
            disabled={
              submitting ||
              (result.retirement.isRetirement ? !result.retirement.retiredSide : result.hasErrors)
            }
            className="px-5 py-2.5 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? 'Submitting…' : 'Submit result for approval'}
          </button>
        </div>

        {/* Info */}
        <div className="mt-4 rounded-md bg-primary-50 text-primary-800 text-sm p-3">
          Submitting records your confirmation. The result then goes to the other team to{' '}
          <strong>approve or dispute</strong>. Points are awarded once approved.
        </div>
      </form>
    </div>
  );
};

export default ResultEntryPage;
